import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

const refreshAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest._retry || error.config.url.includes("/auth/refresh")) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      originalRequest._retry = true;
      try {
        const res = await refreshAPI.post("/auth/refresh");
        const newToken = res.data.accessToken;
        localStorage.setItem("accessToken", newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return API(originalRequest);
      } catch (err) {
        console.error("Refresh failed:", err);
        localStorage.removeItem("accessToken");
        window.location.href = "/";
      }
    }

    return Promise.reject(error);
  },
);

export const registerAgency = (data) => API.post("/auth/register", data);
export const verifyEmail = (token) => API.get(`/auth/verify-email/${token}`);
export const loginUser = (data) => API.post("/auth/login", data);
export const refreshToken = () => API.post("/auth/refresh");
export const logoutUser = () => API.post("/auth/logout");
export const resendVerification = (email) =>
  API.post("/auth/resend-verification", { email });
export const forgotPassword = (email) =>
  API.post("/auth/forgot-password", { email });
export const resetPassword = (data) => API.post("/auth/reset-password", data);
export const submitKyc = (data) => API.post("/kyc/submit", data);
export const updateKyc = (data) => API.patch("/kyc/edit", data);
export const getKyc = () => API.get("/kyc/me");
export const getKycStatus = () => API.get("/kyc/status");
export const reviewKyc = (id, data) => API.put(`/kyc/review/${id}`, data);
// export const adminInvite = (email) => API.post("/auth/admin-invite", { email });
export const adminInvite = (data) => API.post("/auth/admin-invite", data);
export const adminRegister = (data) => API.post("/auth/admin-register", data);
export const getInviteInfo = (token) => API.get(`/auth/invite/${token}`);
export const getProfileData = () => API.get("/profile");
export const uploadProfilePicture = (formData) =>
  API.post("/profile/profile-picture", formData);
export const generateReferralCode = () =>
  API.post("/profile/generate-referral");
export const addAgencyStaff = (data) => API.post("/profile/add-staff", data);
export const getAgencies = () => API.get("/agencies");
export const getAgencyById = (id) => API.get(`/agencies/${id}`);
export const requestPcc = (data) => API.post("/pcc/request", data);
export const getPccRequest = () => API.get("/pcc/");
export const queryPcc = () => API.post("/pcc/query");
export const processPccRequest = (id, data) =>
  API.put(`/pcc/process/${id}`, data);
export const rejectPccRequest = (id, data) =>
  API.put(`/pcc/reject/${id}`, data);
export const getAdminNotifications = () => API.get("/admin/notifications");
export const markNotificationAsRead = (id) =>
  API.patch(`/admin/notifications/${id}/read`);
export const addProduct = (data) => API.post("/products", data);
export const fetchProducts = () => API.get("/products");
export const deleteProduct = (id) => API.delete(`/products/${id}`);
export const hideProduct = (id) => API.patch(`/products/${id}/hide`);
export const reorderProducts = (products) =>
  API.patch("/products/reorder", { products });
export const requestProductDemo = (productId) =>
  API.post(`/products/${productId}/demo-request`);
export const getContract = () => API.get("/contract/me");
export const signContract = (data) => API.post("/contract/sign", data);
export const prepareContract = (agencyId, terms) =>
  API.put(`/contract/${agencyId}/prepare`, { terms });
export const getAdminUsers = (roles) =>
  API.get("/admin/users", { params: { roles } });
export const getAnalytics = () => API.get("/admin/analytics");
export const getAuditLog = (params) => API.get("/admin/audit-log", { params });
export const getAgencyAuditLog = (agencyId) =>
  API.get(`/agencies/${agencyId}/audit-log`);
export const getSalesStaff = () => API.get("/admin/sales-staff");
export const getRegions = () => API.get("/admin/regions");
export const assignAgency = (agencyId, data) =>
  API.patch(`/agencies/${agencyId}/assign`, data);
export const getAgencyEngagement = (agencyId) =>
  API.get(`/agencies/${agencyId}/engagement`);
export const toggleAgencyMilestone = (agencyId, key) =>
  API.patch(`/agencies/${agencyId}/engagement/milestones/${key}`);
export const addAgencyNote = (agencyId, note) =>
  API.post(`/agencies/${agencyId}/engagement/notes`, { note });
export const requestProduct = (productId, quantity) =>
  API.post(`/products/${productId}/request`, { quantity });
